import { useEffect } from 'react'
import { useT } from '../../i18n'
import type { LegacyDuplicateMergePlan } from '../../utils/productionLegacyDuplicateMergePlan'

/**
 * v2.1 finding #36 — confirmation modal for the one-shot legacy duplicate
 * merge migration. Operators see a preview of every group that will be fused
 * (Lot · Date · "N rows → 1 row") before any side effects fire. Cancel leaves
 * everything untouched.
 */
export interface LegacyDuplicateMigrationModalProps {
  plans: LegacyDuplicateMergePlan[]
  running: boolean
  errorMessage: string | null
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}

const formatDate = (raw: string): string => {
  if (!raw) return ''
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return raw
  try {
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return raw.slice(0, 10)
  }
}

export const LegacyDuplicateMigrationModal = ({
  plans,
  running,
  errorMessage,
  onConfirm,
  onCancel
}: LegacyDuplicateMigrationModalProps) => {
  const t = useT()
  const groupCount = plans.length
  const totalAffected = plans.reduce((acc, plan) => acc + plan.group.rows.length, 0)
  const totalDeleted = plans.reduce((acc, plan) => acc + plan.absorbedRows.length, 0)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !running) onCancel()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [onCancel, running])

  return (
    <div
      data-testid="legacy-duplicate-migration-modal"
      role="dialog"
      aria-modal="true"
      aria-label={t('datatable.legacyDuplicates.modal.title')}
      onClick={running ? undefined : onCancel}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1200,
        padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: 12,
          minWidth: 360,
          maxWidth: 640,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)'
        }}
      >
        <header
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <h3 style={{ margin: 0, fontSize: 17, color: '#0f172a' }}>
            {t('datatable.legacyDuplicates.modal.title')}
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#475569' }}>
            {t('datatable.legacyDuplicates.modal.subtitle', {
              groupCount: String(groupCount),
              affected: String(totalAffected),
              kept: String(groupCount),
              deleted: String(totalDeleted)
            })}
          </p>
        </header>
        <div style={{ overflowY: 'auto', padding: '12px 20px' }}>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: '#475569' }}>
            {t('datatable.legacyDuplicates.modal.intro')}
          </p>
          <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: 13, color: '#0f172a' }}>
            {plans.slice(0, 50).map((plan) => (
              <li key={`${plan.group.dayKey}-${plan.group.lotKey}`} style={{ marginBottom: 6 }}>
                {t('datatable.legacyDuplicates.modal.itemFormat', {
                  lot: plan.group.lotLabel,
                  date: formatDate(plan.group.dayKey),
                  from: String(plan.group.rows.length),
                  rebinds: String(plan.logRebinds.length)
                })}
              </li>
            ))}
          </ul>
          {plans.length > 50 && (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
              {t('datatable.legacyDuplicates.modal.truncatedHint', {
                shown: '50',
                total: String(plans.length)
              })}
            </p>
          )}
        </div>
        {errorMessage && (
          <div
            role="alert"
            style={{
              padding: '12px 20px',
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              borderTop: '1px solid #fecaca',
              fontSize: 13
            }}
          >
            {errorMessage}
          </div>
        )}
        <footer
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end'
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={running}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #cbd5f5',
              backgroundColor: '#ffffff',
              color: '#475569',
              fontSize: 13,
              cursor: running ? 'not-allowed' : 'pointer'
            }}
          >
            {t('datatable.legacyDuplicates.modal.cancel')}
          </button>
          <button
            type="button"
            data-testid="legacy-duplicate-migration-confirm"
            onClick={() => void onConfirm()}
            disabled={running || plans.length === 0}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid #0f766e',
              backgroundColor: running ? '#94a3b8' : '#0f766e',
              color: '#ffffff',
              fontSize: 13,
              fontWeight: 600,
              cursor: running || plans.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {running
              ? t('datatable.legacyDuplicates.modal.running')
              : t('datatable.legacyDuplicates.modal.confirm')}
          </button>
        </footer>
      </div>
    </div>
  )
}
